import React, { useState } from 'react';
import styled from 'styled-components';
import { Lock, Eye, EyeOff, Check, ArrowRight } from 'lucide-react';

interface SetPasswordProps {
  onPasswordSet: (password: string) => void;
  loading?: boolean;
}

const SetPassword: React.FC<SetPasswordProps> = ({
  onPasswordSet,
  loading = false
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    onPasswordSet(password);
  };

  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);

  return (
    <Container>
      <HeaderSection>
        <IconCircle>
          <Check size={32} color="#10B981" />
        </IconCircle>
        <Title>Account Verified</Title>
        <Subtitle>Now, let's create a secure password for your account</Subtitle>
      </HeaderSection>
      
      <FormSection>
          <FormGroup>
          <Label htmlFor="password">Create Password</Label>
          <InputWrapper>
            <IconWrapper>
              <Lock size={18} color="#9CA3AF" />
            </IconWrapper>
              <Input
              type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <ToggleButton onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={18} color="#9CA3AF" /> : <Eye size={18} color="#9CA3AF" />}
            </ToggleButton>
          </InputWrapper>
          <HelpText>Password must be at least 8 characters long</HelpText>
          </FormGroup>
        
        <RequirementsBox>
          <RequirementsTitle>Password Requirements:</RequirementsTitle>
          <RequirementsList>
            <RequirementItem>
              <RequirementCheck fulfilled={hasMinLength}>
                {hasMinLength && <Check size={12} color="#10B981" />}
              </RequirementCheck>
              <RequirementText>At least 8 characters</RequirementText>
            </RequirementItem>
            <RequirementItem>
              <RequirementCheck fulfilled={hasUppercase}>
                {hasUppercase && <Check size={12} color="#10B981" />}
              </RequirementCheck>
              <RequirementText>At least one uppercase letter</RequirementText>
            </RequirementItem>
            <RequirementItem>
              <RequirementCheck fulfilled={hasNumber}>
                {hasNumber && <Check size={12} color="#10B981" />}
              </RequirementCheck>
              <RequirementText>At least one number</RequirementText>
            </RequirementItem>
          </RequirementsList>
        </RequirementsBox>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
          <InputWrapper>
            <IconWrapper>
              <Lock size={18} color="#9CA3AF" />
            </IconWrapper>
              <Input
              type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
            />
            <ToggleButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
              {showConfirmPassword ? <EyeOff size={18} color="#9CA3AF" /> : <Eye size={18} color="#9CA3AF" />}
            </ToggleButton>
          </InputWrapper>
          </FormGroup>
      </FormSection>
      
      {error && <ErrorText>{error}</ErrorText>}
      
      <SubmitButton
        onClick={handleSubmit}
        disabled={loading || !(hasMinLength && hasUppercase && hasNumber && password === confirmPassword)}
        isActive={!loading && hasMinLength && hasUppercase && hasNumber && password === confirmPassword}
      >
        <span>Complete Registration</span>
        <ArrowRight size={18} />
      </SubmitButton>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 28rem;
  width: 100%;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const IconCircle = styled.div`
  width: 4rem;
  height: 4rem;
  background-color: #ECFDF5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #7E22CE;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: #4B5563;
  margin-top: 0.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 0.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4B5563;
  margin-bottom: 0.25rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #8B5CF6;
    box-shadow: 0 0 0 1px #8B5CF6;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
`;

const RequirementsBox = styled.div`
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
`;

const RequirementsTitle = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #4B5563;
  margin-bottom: 0.5rem;
  margin-top: 0;
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const RequirementItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
`;

const RequirementCheck = styled.span<{ fulfilled: boolean }>`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.fulfilled ? '#ECFDF5' : '#E5E7EB'};
  color: ${props => props.fulfilled ? '#10B981' : '#9CA3AF'};
`;

const RequirementText = styled.span`
  color: #4B5563;
`;

const ErrorText = styled.div`
  font-size: 0.875rem;
  color: #DC2626;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button<{ isActive: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  cursor: ${props => props.isActive ? 'pointer' : 'not-allowed'};
  transition: background-color 0.2s;
  background-color: ${props => props.isActive ? '#9333EA' : '#D1D5DB'};
  color: ${props => props.isActive ? 'white' : '#6B7280'};
  border: none;

  &:hover {
    background-color: ${props => props.isActive ? '#7E22CE' : '#D1D5DB'};
  }
`;

export default SetPassword; 